/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Wed Dec 11 2024
 **/

import { OBJECT_TYPE } from "../types/enum";

// convert coordinate to coordinateEx
export function makeExCoordinate(point: Vector2): coordinateEx {
	return { ...point, timestamp: performance.now() };
}

// free line 그리기시 자연스러운 곡선을 만들기 위한 좌표 계산후 반환
export function makeNextPoint(prevPoint: Vector2, movement: Vector2) {
	const point = { ...prevPoint };
	if (movement) {
		point.x += movement.x;
		point.y += movement.y;
	}
	return point;
}

// mouse position fit to canvas resolution
export function makeCanvasPoint(canvas: HTMLCanvasElement, position: { x: number; y: number }) {
	const width = canvas.width ?? 1;
	const height = canvas.height ?? 1;
	const innerWidth = canvas.clientWidth ?? 1;
	const innerHeight = canvas.clientHeight ?? 1;
	const ratioX = width / innerWidth;
	const ratioY = height / innerHeight;
	return { x: position.x * ratioX, y: position.y * ratioY };
}

// get clear index
export function getClearIndex(currentIndex: number, objectList: IWhiteboardDrawingObject[]) {
	let clearIndex = -1;
	for (let i = 0; i < currentIndex; ++i) {
		const obj = objectList[i];
		if (obj.objectType === OBJECT_TYPE.EMPTY) {
			clearIndex = i;
		}
	}
	return currentIndex < clearIndex ? -1 : clearIndex;
}

// emoji path to image source
export function loadEmojiIcons(icons: string[]) {
	const emojiImages: CanvasImageSource[] = [];
	icons.map((src) => {
		const icon = new Image();
		icon.src = src;
		emojiImages.push(icon);
	});
	return emojiImages;
}

export function getScaledRectangle(startPosition: Vector2, endPosition: Vector2, imageSize: Resolution) {
	// basic props
	const x = startPosition.x;
	const y = startPosition.y;
	const width = endPosition.x - x;
	const height = endPosition.y - y;

	// image source ratio
	const imageAspectRatio = imageSize.width / imageSize.height;
	const rectAspectRatio = Math.abs(width / height);

	// 드래그 영역과 원본 비율 비교
	let drawWidth, drawHeight;
	if (rectAspectRatio > imageAspectRatio) {
		// 드래그 영역이 더 넓을 경우 - width 기준 height 조정
		drawWidth = Math.abs(width);
		drawHeight = drawWidth / imageAspectRatio;
	} else {
		// 드래그 영역이 더 좁을 경우 - height 기준 width 조정
		drawHeight = Math.abs(height);
		drawWidth = drawHeight * imageAspectRatio;
	}

	// 드래그 방향에 따라 시작점 재계산
	const drawX = width < 0 ? x - drawWidth : x;
	const drawY = height < 0 ? y - drawHeight : y;

	return { x: drawX, y: drawY, width: drawWidth, height: drawHeight };
}

export function getSourceResolution(image: CanvasImageSource | undefined): Resolution {
	if (!image) return { width: 0, height: 0 };
	return {
		width: (image as HTMLImageElement | HTMLCanvasElement).width,
		height: (image as HTMLImageElement | HTMLCanvasElement).height
	};
}
